import { GoalSortingMode, OrderDirection } from '@griply/types';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';

import * as Styled from './sort-bar.style';

const sortOptions: { mode: GoalSortingMode; label: string }[] = [
  {
    mode: GoalSortingMode.Name,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Name}`,
  },
  {
    mode: GoalSortingMode.Progress,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Progress}`,
  },
  {
    mode: GoalSortingMode.Impact,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Impact}`,
  },
  {
    mode: GoalSortingMode.StartDate,
    label: `general.labels.goals.sort.options.${GoalSortingMode.StartDate}`,
  },
  {
    mode: GoalSortingMode.Deadline,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Deadline}`,
  },
  {
    mode: GoalSortingMode.Tasks,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Tasks}`,
  },
];

export type SortColumnProps = {
  mode: GoalSortingMode;
  label: string;
  isActive: boolean;
  direction: OrderDirection;
  onClick: () => void;
};

const SortColumn: React.FC<SortColumnProps> = ({
  mode,
  label,
  isActive,
  direction,
  onClick,
}) => (
  <Styled.Item $mode={mode}>
    <Styled.Button $active={isActive} onClick={onClick}>
      <Typography variant="caption" component="span">
        {label}
      </Typography>
      {isActive && (
        <Styled.DirectionIcon $desc={direction === OrderDirection.Desc}>
          <Icon icon={ArrowUp} />
        </Styled.DirectionIcon>
      )}
    </Styled.Button>
  </Styled.Item>
);

export type SortBarProps = {
  value: GoalSortingMode | undefined;
  direction: OrderDirection;
  onChange: (mode: GoalSortingMode) => void;
  onDirectionToggle: () => void;
  hideLifeArea?: boolean;
};

export const SortBar: React.FC<SortBarProps> = ({
  value,
  direction,
  onChange,
  onDirectionToggle,
  hideLifeArea,
}) => {
  const { t } = useTranslation();

  const showLifeArea = !hideLifeArea;

  const handleClick = (mode: GoalSortingMode) => {
    if (value === mode) {
      onDirectionToggle();
      return;
    }
    onChange(mode);
  };

  const [nameOption, ...restOptions] = sortOptions;
  const onName = () => handleClick(nameOption.mode);
  const onLifeArea = () => handleClick(GoalSortingMode.LifeArea);

  return (
    <Styled.Container>
      <SortColumn
        mode={nameOption.mode}
        label={t(nameOption.label)}
        isActive={value === nameOption.mode}
        direction={direction}
        onClick={onName}
      />

      <Styled.RightGroup>
        {restOptions.map(({ mode, label }) => {
          const handleColumnClick = () => handleClick(mode);
          return (
            <SortColumn
              key={mode}
              mode={mode}
              label={t(label)}
              isActive={value === mode}
              direction={direction}
              onClick={handleColumnClick}
            />
          );
        })}

        {showLifeArea && (
          <SortColumn
            mode={GoalSortingMode.LifeArea}
            label={t(
              `general.labels.goals.sort.options.${GoalSortingMode.LifeArea}`,
            )}
            isActive={value === GoalSortingMode.LifeArea}
            direction={direction}
            onClick={onLifeArea}
          />
        )}

        <Styled.Spacer />
      </Styled.RightGroup>
    </Styled.Container>
  );
};
